import React from "react";
import { usePermission, useCRUDPermission, useCRUDPermissions } from "../hooks/usePermission";

/**
 * Permission Component
 * 
 * Wraps content that should only be visible to users with specific permissions.
 * Supports both legacy string permissions and new CRUD format (resource + action).
 * 
 * @param {string|object|string[]} permission - Permission to check. Can be:
 *   - String: "CREATE_PROPOSAL" (legacy) or "resource:action" (new format)
 *   - Object: { resource: "customer_payments", action: "create" }
 *   - Array: ["CREATE_PROPOSAL", { resource: "customer_payments", action: "read" }]
 * @param {string} resource - Resource name for CRUD permission (e.g., "customer_payments")
 * @param {string} action - Action name for CRUD permission (e.g., "create", "read", "update", "delete" or "c", "r", "u", "d")
 * @param {string|string[]} role - Optional: specific role(s) to check (defaults to user's current role)
 * @param {React.ReactNode} children - Content to render if permission is granted
 * @param {React.ReactNode} fallback - Optional: Content to render if permission is not granted
 * @param {boolean} requireAll - If true, requires ALL permissions (AND logic). If false, requires ANY permission (OR logic). Default: false
 * @param {boolean} showIfNoPermission - If true, shows content even if no permission data is available. Default: false
 */
const Permission = ({
  permission,
  resource,
  action,
  role,
  children,
  fallback = null,
  requireAll = false,
  showIfNoPermission = false,
}) => {
  // Determine which mode to use, but always call hooks unconditionally
  // CRUD format with array of actions
  const useMultipleActions = resource && Array.isArray(action);
  // CRUD format with single action
  const useSingleAction = resource && action && !Array.isArray(action);
  // Legacy format
  const useLegacyPermission = permission && !resource;

  // Always call hooks unconditionally (React rules requirement)
  // Call useCRUDPermissions for multiple actions (even if not used)
  const crudMultipleResult = useCRUDPermissions(
    useMultipleActions ? resource : null,
    useMultipleActions ? action : [],
    requireAll
  );

  // Call useCRUDPermission for single action (even if not used)
  const crudSingleResult = useCRUDPermission(
    useSingleAction ? resource : null,
    useSingleAction ? action : null
  );

  // Call usePermission for legacy format (even if not used)
  const legacyResult = usePermission(
    useLegacyPermission ? permission : null,
    useLegacyPermission ? role : null,
    requireAll
  );

  // Determine which result to use based on the mode
  let hasPermission = false;
  let isLoading = false;

  if (useMultipleActions) {
    hasPermission = crudMultipleResult.hasPermission;
    isLoading = crudMultipleResult.isLoading;
  } else if (useSingleAction) {
    hasPermission = crudSingleResult.hasPermission;
    isLoading = crudSingleResult.isLoading;
  } else if (useLegacyPermission) {
    hasPermission = legacyResult.hasPermission;
    isLoading = legacyResult.isLoading;
  } else {
    // No permission specified, don't show
    return <>{fallback}</>;
  }

  // If still loading and we shouldn't show if no permission, return null
  if (isLoading && !showIfNoPermission) {
    return null;
  }

  // If loading and we should show if no permission, show children
  if (isLoading && showIfNoPermission) {
    return <>{children}</>;
  }

  // If permission is granted, show children
  if (hasPermission) {
    return <>{children}</>;
  }

  // Otherwise, show fallback or nothing
  return <>{fallback}</>;
};

export default Permission;
